import { useMemo } from 'react';
import { useActivationId } from '@atlassian/jira-tenant-context-controller/src/components/activation-id/index.tsx';
import { createAri, getAriConfig, toAri } from '@atlassian/jira-platform-ari/src/index.tsx';

/**
 * Returns the activation type ARI for the sprint.
 *
 * Converts backend sprint ARIs to activation-based ARIs. Backend generally uses the format
 * `ari:cloud:jira-software:<cloudId>:sprint/<resourceId>` in its response while some APIs expect the activation
 * format `ari:cloud:jira:<cloudId>:sprint/activation/<activationId>/<resourceId>`.
 *
 * @param sprintId - The ID of the sprint in the format `ari:cloud:jira-software:<cloudId>:sprint/<resourceId>`.
 * @returns The ARI for the sprint in the format `ari:cloud:jira:<cloudId>:sprint/activation/<activationId>/<resourceId>`.
 */
export const useSprintAri = (sprintId?: string): string => {
	const activationId = useActivationId();

	const sprintAri = useMemo(() => {
		if (!sprintId || !activationId) {
			return '';
		}
		const ari = toAri(sprintId);
		if (!ari) {
			return '';
		}

		const ariConfig = getAriConfig(ari);
		const { cloudId, resourceId } = ariConfig;

		if (!cloudId || !resourceId) {
			return '';
		}

		return createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'sprint',
			resourceId,
			activationId,
		});
	}, [sprintId, activationId]);

	return sprintAri;
};
